import { ErrorLink } from '@apollo/client/link/error'
import { createClient } from 'graphql-ws'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { SetContextLink } from '@apollo/client/link/context'
import { getMainDefinition } from '@apollo/client/utilities'
import { getAccessToken, clearAccessToken } from 'lib/auth'
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client'
// types
import { AppErrorKind } from 'types/errors'
import { AuthErrorCode } from 'types/auth/helpers'
// utils
import { normalizeError } from 'utils/errors/normalize'
import { notifyError, reportError } from 'utils/errors/handlers'
// store
import { store } from 'store/store'
import { LOGOUT_ACTION } from 'store/types'

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GQL_API_URL,
  credentials: 'include'
})

const cache = new InMemoryCache({ possibleTypes: { UserOrPosition: ['User', 'Position'] } })

const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GQL_WS_URL,
    lazy: true,
    connectionParams: () => {
      const accessToken = getAccessToken()

      return {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : ''
        }
      }
    }
  })
)

const authLink = new SetContextLink(({ headers }) => {
  const accessToken = getAccessToken()

  return {
    headers: {
      ...headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` })
    }
  }
})

let isRedirectingToLogin = false

const handleSessionError = (codes: string[]) => {
  for (const code of codes) {
    switch (code) {
      case AuthErrorCode.UNAUTHENTICATED:
      case AuthErrorCode.SESSION_TOKEN_REUSE:
      case AuthErrorCode.SESSION_EXPIRED:
      case AuthErrorCode.SESSION_REVOKED:
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/public/')) {
          break
        }
        clearAccessToken()
        store.dispatch({ type: LOGOUT_ACTION })
        if (typeof window !== 'undefined' && !isRedirectingToLogin && !window.location.pathname.startsWith('/login')) {
          isRedirectingToLogin = true
          window.location.href = '/login'
        }
        break

      case AuthErrorCode.TWO_FACTOR_REQUIRED:
        // SKEFTO_ADMIN without 2FA — let the component handle the error message
        break

      case AuthErrorCode.TOKEN_EXPIRED:
        // Handled by SessionProvider auto-refresh; no action needed here
        break
    }
  }
}

const isMutation = (operation: ApolloLink.Operation) => {
  const definition = getMainDefinition(operation.query)

  return definition.kind === 'OperationDefinition' && definition.operation === 'mutation'
}

// Default handling for every failed operation: log it, and for mutations tell the user.
// A caller that shows the error itself (form error, own snackbar) opts out of the snackbar
// with `context: { handledLocally: true }`.
const createErrorLink = ({ handleSession }: { handleSession: boolean }) =>
  new ErrorLink(({ error, operation }) => {
    const appError = normalizeError(error)

    if (appError.kind === AppErrorKind.AUTH) {
      if (handleSession) {
        handleSessionError(appError.codes)
      }

      return
    }

    reportError(appError, operation.operationName ?? 'GraphQL')

    if (isMutation(operation) && !operation.getContext().handledLocally) {
      notifyError(appError)
    }
  })

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  ApolloLink.from([createErrorLink({ handleSession: true }), authLink, httpLink])
)

const createApolloClient = () => {
  return new ApolloClient({
    cache,
    link: splitLink
  })
}

const createPublicApolloClient = () => {
  // Separate cache instance to avoid polluting the authenticated client's
  // normalized cache with unauthenticated (login/public form) responses.
  const publicCache = new InMemoryCache({ possibleTypes: { UserOrPosition: ['User', 'Position'] } })

  return new ApolloClient({
    cache: publicCache,
    // The public client serves the auth pages: a failed sign-in must not trigger logout/redirect
    link: ApolloLink.from([createErrorLink({ handleSession: false }), httpLink]),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false
      }
    }
  })
}

export const client = createApolloClient()
export const publicClient = createPublicApolloClient()
